import { Controller } from "@hotwired/stimulus";

// Drives the affiliate "Provisionen" product list. A paid row shows what applies to it:
// the standard as a prefilled value while it follows the standard, its own value (0
// included) when it deviates, and nothing ("–") when it is excluded. The server reads
// the very same values back (AffiliatePrograms::ProductsForm#desired_rule_attributes),
// so no hidden flag is posted and the state survives a validation-error re-render.
// Three jobs:
//   1. Standard sync — a new standard carries every paid row that still follows the
//      previous one; rows the creator set themselves stay as they are.
//   2. Customized state — a paid row is customized while it deviates from the standard,
//      a free row while its Plus/Check is selected. Drives the revert button and the
//      "customized" filter (via rowschanged).
//   3. Revert — puts a paid row back onto the standard.
export default class extends Controller {
  static targets = ["globalInput"];

  connect() {
    this.paidRows.forEach((row) => this.markFollowing(row));
    this.refreshAll();
  }

  // input on a standard cell -> the rows following the standard take the new one.
  // Whether a row follows is only re-derived when the row itself changes: a row that
  // is empty because it is excluded must not be swept along while the standard passes
  // through empty on its way to a new value.
  syncStandard() {
    const cells = this.standardCells;
    this.paidRows
      .filter((row) => row.dataset.followsStandard === "true")
      .forEach((row) => this.fill(row, cells));
    this.refreshAll();
  }

  // input on a row cell or a free toggle -> recompute that row.
  rowChanged(event) {
    const row = this.rowFor(event.target);
    if (!row) return;

    if (this.isPaid(row)) this.markFollowing(row);
    this.refreshRow(row);
    this.dispatch("rowschanged");
  }

  // revert button -> back onto the standard. Dispatches input like typing would, so
  // the editor marks the sheet dirty and #rowChanged re-derives the row.
  revert(event) {
    const row = this.rowFor(event.target);
    if (!row) return;

    this.fill(row, this.standardCells);
    this.rowInputs(row).forEach((input) => {
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });
  }

  // change on the multi-level switch -> the number of compared tiers changed, so
  // whether a row follows is decided anew: single level cuts both ladders to tier 1,
  // and a row that only differed on tier 2 follows the standard from now on. An
  // empty row keeps what it was: while the standard pays nothing, a following row is
  // empty too, and an excluded row must not be taken for one.
  multiLevelChanged() {
    this.paidRows
      .filter((row) => this.rowLadder(row).length > 0)
      .forEach((row) => this.markFollowing(row));
    this.refreshAll();
  }

  refreshAll() {
    this.rows.forEach((row) => this.refreshRow(row));
    this.dispatch("rowschanged");
  }

  refreshRow(row) {
    if (!row) return;

    const customized = this.isCustomized(row);
    row.dataset.customized = customized ? "true" : "false";

    const button = row.querySelector("[data-affiliate-products-revert]");
    if (button) button.disabled = !customized;
  }

  markFollowing(row) {
    row.dataset.followsStandard = this.matchesStandard(row) ? "true" : "false";
  }

  isCustomized(row) {
    if (this.isPaid(row)) return !this.matchesStandard(row);

    const toggle = row.querySelector("input[data-affiliate-free-toggle]");
    return !!toggle && toggle.value.trim() !== "";
  }

  matchesStandard(row) {
    const rowLadder = this.rowLadder(row);
    const standard = this.ladder(this.standardCells);

    return rowLadder.length === standard.length &&
      rowLadder.every((value, index) => value === standard[index]);
  }

  rowLadder(row) {
    return this.ladder(this.rowInputs(row).map((input) => input.value));
  }

  fill(row, cells) {
    this.rowInputs(row).forEach((input, index) => {
      input.value = cells[index] ?? "";
    });
  }

  // The cells a following row shows: the standard when it pays something, otherwise
  // nothing — a standard of 0, or none, includes no paid row.
  get standardCells() {
    const cells = this.globalInputTargets.map((input) => input.value.trim());
    return Number.parseInt(cells[0], 10) > 0 ? cells : cells.map(() => "");
  }

  // Comparable form of a row's cells, read like the server reads them: integers,
  // trailing blanks are unused cells, and single level compares tier 1 only.
  ladder(cells) {
    const values = (this.multiLevel ? cells : cells.slice(0, 1)).map((value) => {
      const number = Number.parseInt(String(value).trim(), 10);
      return Number.isNaN(number) ? null : number;
    });
    while (values.length > 0 && values[values.length - 1] === null) values.pop();
    return values;
  }

  get multiLevel() {
    return !!this.element.querySelector('input[type="checkbox"][name$="[multi_level]"]')?.checked;
  }

  get rows() {
    return Array.from(this.element.querySelectorAll("[data-affiliate-products-row]"));
  }

  get paidRows() {
    return this.rows.filter((row) => this.isPaid(row));
  }

  isPaid(row) {
    return this.rowInputs(row).length > 0;
  }

  rowFor(element) {
    return element.closest("[data-affiliate-products-row]");
  }

  rowInputs(row) {
    return Array.from(row.querySelectorAll('input[type="number"]'));
  }
}
